import {
    Overlay,
    createOverlay,
} from '@vfde-brix/ws10/overlay';
import { HtmlId } from '../enums/elementId';
import { mountComponent } from '../util/mount';
import { OverlayContent } from '../container/Overlay/enum';
import {
    setElementVisibility,
    setElementsVisibility,
} from '../util/visibility';
import { resetIconsPosition } from './fullwidthNotification';

const SCROLL_CLASS = 'ws10-overlay-fix';

/**
 * The overlay component
 */
let overlay: Overlay | null = null;

/**
 * Set overlay component
 *
 * @param {Overlay} newOverlay - Overlay to set
 */
const setOverlay = (newOverlay: Overlay) => {
    overlay = newOverlay;
};

/**
 * Get overlay component
 *
 * @returns {Overlay|null} Overlay component or null if not set
 */
export const getOverlay = (): Overlay | null => overlay;

/**
 * Create Brix Overlay component.
 *
 * @param {HtmlId} id - Wrapper ID of the element.
 * @param {Function} onClose - Function used to show/hide the overlay.
 * @returns {Overlay|null} Overlay component or null if element with given id is not found.
 */
// eslint-disable-next-line @typescript-eslint/ban-types
export const mountOverlay = (id: HtmlId, onClose: Function): Overlay | null => {
    const comp = mountComponent(id, createOverlay, {
        optPaddingLayout: true,
        optAnimation: 'slide',
        business: {
            onClose: () => {
                onClose();

                /**
                 * Add this class to the html and body tags to fix
                 * the overflow: hidden not taking effect bug on iOS devices.
                 * The class itself contains media queries to be mobile-only,
                 * so it has no effect on bigger screens
                 */
                document.documentElement.classList.remove(SCROLL_CLASS);
                document.body.classList.remove(SCROLL_CLASS);
                resetIconsPosition();
            },
            onOpen: () => {
                document.documentElement.classList.add(SCROLL_CLASS);
                document.body.classList.add(SCROLL_CLASS);
            },
        },
    });

    if (comp) {
        setOverlay(comp);
    }

    return overlay;
};

/**
 * Hides all of the container elements inside the overlay, then
 * shows the selected one.
 *
 * @param activeContent - ID of the container to show
 * inside the overlay.
 */
export const setOverlayActiveContent = (activeContent: OverlayContent) => {
    const elementIds = Object.values(OverlayContent).map(id => id.toString());

    setElementsVisibility(elementIds, false);
    setElementVisibility(activeContent.toString());
};

/**
 * Toggles the visibility of the Brix Overlay component.
 *
 * @param isVisible - Show/hide the component.
 */
export const setOverlayVisibility = (isVisible: boolean) => {
    if (overlay) {
        isVisible ? overlay.open() : overlay.close();
    }
};
